import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorAlert } from '../../components/common/ErrorAlert';
import { LoadingState } from '../../components/common/LoadingState';
import { EngagementPeopleDrawer } from '../../components/common/EngagementPeopleDrawer';
import { AlertCard } from '../../components/alerts/AlertCard';
import { ShellCard } from '../../components/layout/ShellCard';
import { useFeedback } from '../../components/common/FeedbackProvider';
import { useAdminAlerts } from '../../hooks/useAdminAlerts';
import { useAuth } from '../../hooks/useAuth';
import { pagesService } from '../../services/pagesService';
import { queryKeys } from '../../api/queryClient';
import { NOTIFICATION_KINDS } from '../../utils/constants';
import { formatDateTime } from '../../utils/format';
import { alertKindLabel, roleLabel } from '../../utils/labels';
import { deriveNotificationKind } from '../../utils/notificationKind';
import {
  canComposeAlerts,
  composeAudienceOptions,
  defaultComposeAudience,
} from '../../utils/roles';
import { tokens } from '../../theme/tokens';

const COMPOSE_TYPES = [
  { id: 'message', label: 'Message' },
  { id: 'photo', label: 'Photo' },
  { id: 'media', label: 'Video or audio' },
  { id: 'reply', label: 'Needs a reply' },
];

export function AlertsTab() {
  const { user } = useAuth();
  const { showSuccess, showError } = useFeedback();
  const role = user.role;
  const {
    items,
    loading,
    error,
    busy,
    q,
    setQ,
    kind,
    setKind,
    setError,
    load,
    create,
  } = useAdminAlerts();

  const audienceOptions = useMemo(() => composeAudienceOptions(role), [role]);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [articleId, setArticleId] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [mediaUrl, setMediaUrl] = useState('');
  const [mediaCaption, setMediaCaption] = useState('');
  const [externalUrl, setExternalUrl] = useState('');
  const [requireAction, setRequireAction] = useState(false);
  const [composeType, setComposeType] = useState('message');
  const [peopleDrawer, setPeopleDrawer] = useState(null);
  const [audienceRoles, setAudienceRoles] = useState(() =>
    defaultComposeAudience(role),
  );

  const publishedQuery = useQuery({
    queryKey: queryKeys.adminContent('published'),
    queryFn: () => pagesService.adminContent('published'),
    enabled: canComposeAlerts(role),
  });
  const publishedItems = publishedQuery.data?.items ?? [];

  const draftActions = useMemo(() => {
    const primary = articleId.trim()
      ? [
          {
            id: 'open',
            label: 'Read story',
            style: 'primary',
            actionType: 'open_article',
            articleId: articleId.trim(),
          },
        ]
      : externalUrl.trim()
        ? [
            {
              id: 'open',
              label: 'Open link',
              style: 'primary',
              actionType: 'open_url',
              url: externalUrl.trim(),
            },
          ]
        : requireAction
          ? [
          {
            id: 'ack',
            label: 'Got it',
            style: 'primary',
            actionType: 'custom',
            customKey: 'ack',
          },
            ]
          : [];

    if (!requireAction) return primary.length ? primary : undefined;

    return [
      ...primary,
      {
        id: 'dismiss',
        label: 'Dismiss',
        style: 'secondary',
        actionType: 'dismiss',
      },
    ];
  }, [articleId, externalUrl, requireAction]);

  const detectedKind = useMemo(
    () =>
      deriveNotificationKind({
        imageUrl,
        mediaUrl,
        actions: draftActions,
      }),
    [imageUrl, mediaUrl, draftActions],
  );

  function toggleAudience(value) {
    setAudienceRoles((prev) => {
      if (value === 'all') {
        return prev.includes('all') ? [] : ['all'];
      }
      const withoutAll = prev.filter((r) => r !== 'all');
      if (withoutAll.includes(value)) {
        return withoutAll.filter((r) => r !== value);
      }
      return [...withoutAll, value];
    });
  }

  async function onSend(e) {
    e.preventDefault();
    if (!canComposeAlerts(role)) return;
    if (audienceRoles.length === 0) {
      showError('Select at least one audience');
      return;
    }
    const image = imageUrl.trim() || undefined;
    const media = mediaUrl.trim() || undefined;
    const rawArticle = articleId.trim();
    const article =
      !rawArticle ||
      ['none', 'null', 'undefined'].includes(rawArticle.toLowerCase())
        ? undefined
        : rawArticle;
    const url = externalUrl.trim() || undefined;

    const actions = draftActions?.length ? draftActions : undefined;

    const kind = deriveNotificationKind({
      imageUrl: image,
      mediaUrl: media,
      actions,
    });

    try {
      console.log(
        'PRASAAR_PUSH admin send request',
        JSON.stringify({
          title: title.trim(),
          kind,
          audienceRoles,
          articleId: article || null,
          hasImage: Boolean(image),
          hasMedia: Boolean(media),
          url: url || null,
          requireAction,
          actionCount: actions?.length ?? 0,
        }),
      );
      const created = await create({
        title: title.trim(),
        message: message.trim(),
        kind,
        imageUrl: image,
        mediaUrl: media,
        mediaCaption: mediaCaption.trim() || undefined,
        articleId: article,
        url,
        deepLink: article ? `/article/${article}` : undefined,
        audienceRoles,
        actions,
      });
      setTitle('');
      setMessage('');
      setArticleId('');
      setImageUrl('');
      setMediaUrl('');
      setMediaCaption('');
      setExternalUrl('');
      setRequireAction(false);
      setComposeType('message');
      setAudienceRoles(defaultComposeAudience(role));
      const push = created?.push;
      const pushNote = (() => {
        if (!push || typeof push.sent !== 'number') return '';
        const pruned =
          typeof push.pruned === 'number' && push.pruned > 0
            ? ` (${push.pruned} expired removed)`
            : '';
        if (push.sent > 0) {
          return ` · push to ${push.sent}/${push.devices} device${push.devices === 1 ? '' : 's'}${pruned}`;
        }
        if (push.reason === 'no_devices') {
          return ' · push missed (no devices — open Feed after sign-in to allow notifications)';
        }
        if (push.reason === 'no_recipients') {
          return ' · push missed (no users in selected audience)';
        }
        if (push.reason === 'vapid_disabled') {
          return ' · phone alerts are not available on the server right now';
        }
        if (push.reason === 'all_expired') {
          return ' · push missed (all device subscriptions expired — users must Enable alerts again)';
        }
        if (push.reason === 'send_failed' || (push.devices > 0 && push.sent === 0)) {
          const detail = push.detail ? ` (${push.detail})` : '';
          const hint = push.hint ? ` — ${push.hint}` : '';
          return ` · push missed (send failed on ${push.failed ?? push.devices} device${(push.failed ?? push.devices) === 1 ? '' : 's'}${detail})${hint}${pruned}`;
        }
        return ` · push missed (${push.reason || 'unknown'})`;
      })();
      console.log(
        'PRASAAR_PUSH admin send response',
        JSON.stringify({
          notificationId: created?.id ?? null,
          push: created?.push ?? null,
        }),
      );
      showSuccess(`Alert sent${pushNote}`);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Send failed');
    }
  }

  return (
    <Box>
      <ErrorAlert message={error} onClose={() => setError(null)} />

      {!canComposeAlerts(role) && (
        <EmptyState
          description="Only admins and checkers can compose broadcast alerts. Makers create feed posts for review instead."
        />
      )}

      {canComposeAlerts(role) && (
        <ShellCard sx={{ mb: { xs: 1.5, sm: 2 } }}>
          <Box component="form" onSubmit={onSend}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 1.5 }}
              flexWrap="wrap"
              useFlexGap
              spacing={1}
            >
              <Typography
                variant="h6"
                sx={{ fontSize: { xs: '1.05rem', sm: '1.25rem' } }}
              >
                Compose alert
              </Typography>
              <Chip
                size="small"
                color="secondary"
                label={alertKindLabel(detectedKind)}
              />
            </Stack>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2, fontSize: { xs: '0.9375rem', sm: '1rem' } }}
            >
              Write a short title and message. Add a photo or a story if you want
              people to tap through.
            </Typography>
            <Stack spacing={{ xs: 1.5, sm: 2 }}>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  What are you sending?
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {COMPOSE_TYPES.map((t) => (
                    <Chip
                      key={t.id}
                      clickable
                      label={t.label}
                      color={composeType === t.id ? 'secondary' : 'default'}
                      variant={composeType === t.id ? 'filled' : 'outlined'}
                      onClick={() => {
                        setComposeType(t.id);
                        setRequireAction(t.id === 'reply');
                        if (t.id === 'message') {
                          setImageUrl('');
                          setMediaUrl('');
                        }
                        if (t.id === 'photo') setMediaUrl('');
                      }}
                    />
                  ))}
                </Stack>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Who should receive it?
                </Typography>
                <FormGroup
                  row
                  sx={{
                    gap: { xs: 0, sm: 0.5 },
                    flexWrap: 'wrap',
                    '& .MuiFormControlLabel-root': {
                      mr: { xs: 1.5, sm: 2 },
                      ml: 0,
                    },
                  }}
                >
                  {audienceOptions.map((opt) => (
                    <FormControlLabel
                      key={opt.value}
                      control={
                        <Checkbox
                          checked={audienceRoles.includes(opt.value)}
                          onChange={() => toggleAudience(opt.value)}
                        />
                      }
                      label={opt.label}
                    />
                  ))}
                </FormGroup>
                {role === 'checker' ? (
                  <Typography variant="caption" color="text.secondary">
                    Readers and writers only. An admin can reach staff.
                  </Typography>
                ) : null}
              </Box>
              <TextField
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                fullWidth
                multiline
                rows={3}
                InputLabelProps={{ shrink: true }}
              />
              {(composeType === 'photo' || composeType === 'media') && (
                <TextField
                  label="Photo URL"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  placeholder="https://…"
                  fullWidth
                  helperText="Use an https photo. It shows on the phone lock screen and in Alerts."
                  InputLabelProps={{ shrink: true }}
                />
              )}
              {composeType === 'media' && (
                <>
                  <TextField
                    label="Video or audio link"
                    value={mediaUrl}
                    onChange={(e) => setMediaUrl(e.target.value)}
                    placeholder="https://….mp4 · YouTube · https://….mp3"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="Caption (optional)"
                    value={mediaCaption}
                    onChange={(e) => setMediaCaption(e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </>
              )}
              <FormControl fullWidth>
                <InputLabel id="story-link-label" shrink>
                  Link a live story (optional)
                </InputLabel>
                <Select
                  labelId="story-link-label"
                  label="Link a live story (optional)"
                  notched
                  value={articleId}
                  onChange={(e) => setArticleId(e.target.value)}
                >
                  <MenuItem value="">None</MenuItem>
                  {publishedItems.map((story) => (
                    <MenuItem key={story.id} value={story.id}>
                      {story.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Or an external link (optional)"
                value={externalUrl}
                onChange={(e) => setExternalUrl(e.target.value)}
                placeholder="https://…"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              {title.trim() && message.trim() ? (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Preview
                  </Typography>
                  <AlertCard
                    item={{
                      id: 'preview',
                      title: title.trim(),
                      message: message.trim(),
                      kind: detectedKind,
                      imageUrl: imageUrl.trim() || undefined,
                      mediaUrl: mediaUrl.trim() || undefined,
                      mediaCaption: mediaCaption.trim() || undefined,
                      read: true,
                      actions: draftActions,
                    }}
                  />
                </Box>
              ) : null}
              <Button
                type="submit"
                variant="contained"
                color="secondary"
                disabled={busy}
                sx={{
                  color: '#fff',
                  alignSelf: { xs: 'stretch', sm: 'flex-start' },
                }}
              >
                Send alert
              </Button>
            </Stack>
          </Box>
        </ShellCard>
      )}

      <ShellCard
        sx={{ overflow: 'hidden', mb: 0 }}
        contentSx={{ p: 0, '&:last-child': { pb: 0 } }}
      >
        <Box
          sx={{
            px: { xs: 1.5, sm: 2 },
            py: { xs: 1.5, sm: 1.75 },
            borderBottom: `1px solid ${tokens.border}`,
          }}
        >
          <Typography
            variant="h6"
            sx={{ mb: 0.5, fontSize: { xs: '1.05rem', sm: '1.25rem' } }}
          >
            Sent history
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1.5, fontSize: { xs: '0.9375rem', sm: '1rem' } }}
          >
            Alerts you sent. Review and publish notices live in Content.
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.25}
            alignItems={{ sm: 'flex-end' }}
          >
            <TextField
              size="small"
              label="Search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void load();
              }}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <FormControl
              size="small"
              sx={{ minWidth: { xs: '100%', sm: 160 } }}
            >
              <InputLabel id="filter-kind-label" shrink>
                Kind
              </InputLabel>
              <Select
                labelId="filter-kind-label"
                label="Kind"
                notched
                value={kind}
                onChange={(e) => {
                  const next = e.target.value;
                  setKind(next);
                  void load(q, next);
                }}
              >
                {NOTIFICATION_KINDS.map((k) => (
                  <MenuItem key={k} value={k}>
                    {k === 'all' ? 'All types' : alertKindLabel(k)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              onClick={() => void load()}
              sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}
            >
              Search
            </Button>
          </Stack>
        </Box>

        {loading && (
          <Box sx={{ p: 2 }}>
            <LoadingState />
          </Box>
        )}

        {!loading &&
          items.map((item, index) => (
            <Box key={item.id}>
              {index > 0 ? <Divider /> : null}
              <Box sx={{ px: { xs: 1.75, sm: 2.25 }, py: { xs: 1.75, sm: 2 } }}>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ mb: 1 }}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Chip size="small" color="primary" label={alertKindLabel(item.kind)} />
                  {(item.audienceRoles?.length
                    ? item.audienceRoles
                    : item.targetUserIds?.length
                      ? ['personal']
                      : []
                  ).map((a) => (
                    <Chip
                      key={a}
                      size="small"
                      variant="outlined"
                      label={
                        a === 'all'
                          ? 'Everyone signed in'
                          : a === 'personal'
                            ? 'One person'
                            : roleLabel(a) || a
                      }
                    />
                  ))}
                </Stack>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 0.5,
                    fontSize: { xs: '1.1rem', sm: '1.2rem' },
                    wordBreak: 'break-word',
                  }}
                >
                  {item.title}
                </Typography>
                <Typography
                  sx={{
                    mb: 1,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    wordBreak: 'break-word',
                  }}
                >
                  {item.message}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.75 }}
                >
                  {item.sentByName ? `by ${item.sentByName} · ` : ''}
                  {formatDateTime(item.createdAt)}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1, fontSize: { xs: '0.875rem', sm: '0.9375rem' } }}
                >
                  {(() => {
                    const n = item.pushDevices ?? 0;
                    const noun = n === 1 ? 'device' : 'devices';
                    const delivered =
                      typeof item.pushSent === 'number'
                        ? ` · ${item.pushSent} delivered`
                        : '';
                    return `Sent to ${n} ${noun}${delivered}`;
                  })()}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      setPeopleDrawer({ id: item.id, kind: 'opens' })
                    }
                  >
                    Opened by {(item.engagement?.opens ?? 0).toLocaleString()}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      setPeopleDrawer({ id: item.id, kind: 'clicks' })
                    }
                  >
                    Clicked by {(item.engagement?.clicks ?? 0).toLocaleString()}
                  </Button>
                </Stack>
              </Box>
            </Box>
          ))}

        {!loading && items.length === 0 && (
          <Box sx={{ p: 2 }}>
            <EmptyState description="No alerts sent yet." />
          </Box>
        )}
      </ShellCard>
      <EngagementPeopleDrawer
        open={Boolean(peopleDrawer)}
        onClose={() => setPeopleDrawer(null)}
        notificationId={peopleDrawer?.id}
        kind={peopleDrawer?.kind || 'opens'}
      />
    </Box>
  );
}
